import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { format, parseISO, addDays, startOfWeek } from "date-fns";
import { sk } from "date-fns/locale";
import JumboSearch from "@jumbo/components/JumboSearch";
import AddBoxIcon from "@mui/icons-material/AddBox";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StayCurrentPortraitIcon from "@mui/icons-material/StayCurrentPortrait";
import StayCurrentLandscapeIcon from "@mui/icons-material/StayCurrentLandscape";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import { Link, useNavigate } from "react-router-dom";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { useSnackbar } from "notistack";
import {
  getDailyMenuList,
  getDailyMenuById,
  getDailyMenuSettings,
  deleteDailyMenuById,
  refreshDailyMenuScreen,
  publishDailyMenuWebExport,
} from "app/services/api/dailyMenu";
import {
  createNewMenu,
  deleteMenu as deleteMenuFromStorage,
  getStoredMenu,
  saveMenu,
} from "app/data/dailyMenuAdmin.storage";
import { getSettings as getDailyMenuScreenSettings } from "app/data/dailyMenuScreenSettings.storage";
import { getDailyMenuScreenPath } from "app/helpers/dsok/dailyMenuUrl";
import { DAILY_MENU_TYPES } from "app/utils/constants/settings";

/** Poradie riadkov vo WEB Export dialógu: aktuálny týždeň (_2) najprv, potom nasledujúci (_1). */
const WEB_EXPORT_ROW_ORDER = [
  "breakfast_2",
  "lunch_2",
  "dinner_2",
  "breakfast_1",
  "lunch_1",
  "dinner_1",
];

/** Zobrazovaný názov typu menu (Raňajky/Obed/Večera) podľa kódu. */
function getMenuTypeLabel(code) {
  const found = DAILY_MENU_TYPES.find((typ) => typ.code === code);
  return found ? found.name : "";
}

/** Pondelok aktuálneho týždňa v ISO (yyyy-MM-dd). */
function getThisWeekMondayIso() {
  const now = new Date();
  const monday = startOfWeek(now, { weekStartsOn: 1 });
  return format(monday, "yyyy-MM-dd");
}

/** Z weekStartDate (ISO pondelka) vráti reťazec "Od d.M. - Do d.M.yyyy". */
function getWeekRangeDisplay(weekStartDate, locale = sk) {
  if (!weekStartDate || typeof weekStartDate !== "string") return "—";
  try {
    const mon = parseISO(weekStartDate);
    const sun = addDays(mon, 6);
    return `${format(mon, "d.M.", { locale })} – ${format(sun, "d.M.yyyy", { locale })}`;
  } catch {
    return "—";
  }
}

const DsokDailyMenuList = () => {
  const { t, i18n } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [searchName, setSearchName] = useState("");
  const [menus, setMenus] = useState([]);
  const [screenSettings, setScreenSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copyingMenuId, setCopyingMenuId] = useState(null);
  const [refreshingScreen, setRefreshingScreen] = useState(false);
  const [deletingMenuId, setDeletingMenuId] = useState(null);
  const [publishingWebExport, setPublishingWebExport] = useState(false);
  const [webExportResult, setWebExportResult] = useState(null);
  const navigate = useNavigate();

  // customerCode z localStorage: role.customer.dsokSettings.customer.code (kľúč "user")
  const userFromStorage = JSON.parse(localStorage.getItem("user") || "{}");
  const customerCodeFromStorage =
    userFromStorage?.customer?.dsokSettings?.customer?.code ?? "";
  const beDomain =
    userFromStorage?.customer?.dsokSettings?.customer?.beDomain ?? "";
  const enableWebExport =
    userFromStorage?.customer?.dsokSettings?.customer?.customerUISettings
      ?.enableDailyMenuWebExport === true;
  const firstMenu = menus[0];
  const customerCodeFromMenu = firstMenu?.customerCode ?? "";
  const customerCode =
    String(
      customerCodeFromStorage || customerCodeFromMenu || "default",
    ).trim() || "default";
  const locationCode = "main";
  let env = "prod";
  env = beDomain.includes("staging") ? "stage" : "prod";
  env = window.location.origin.includes("localhost") ? "dev" : env;
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  /** Cesta pre daily-menu-screen (bez origin), env ako query ?env=xxx. */
  const getScreenPathForMenu = (menuId, orientation) =>
    getDailyMenuScreenPath({
      customerCode,
      locationCode,
      settings: screenSettings,
      menuId: menuId || "default",
      orientation: orientation === "portrait" ? "portrait" : "landscape",
    });

  // Šablóna sa berie z nastavení obrazovky (settings.template, naživo z by-code), nie z URL,
  // preto ju do URL obrazoviek už nepridávame.
  const screenQuery = `env=${encodeURIComponent(env)}`;
  const menuIdForScreen = firstMenu?.id ?? firstMenu?.idZaznamu ?? "default";
  const dailyMenuScreenLandscapePath = getScreenPathForMenu(
    menuIdForScreen,
    "landscape",
  );
  const dailyMenuScreenPortraitPath = getScreenPathForMenu(
    menuIdForScreen,
    "portrait",
  );
  const dailyMenuScreenLandscapeUrl =
    dailyMenuScreenLandscapePath && origin
      ? `${origin.replace(/\/$/, "")}/${dailyMenuScreenLandscapePath}?${screenQuery}`
      : dailyMenuScreenLandscapePath
        ? `/${dailyMenuScreenLandscapePath}?${screenQuery}`
        : "";
  const dailyMenuScreenPortraitUrl =
    dailyMenuScreenPortraitPath && origin
      ? `${origin.replace(/\/$/, "")}/${dailyMenuScreenPortraitPath}?${screenQuery}`
      : dailyMenuScreenPortraitPath
        ? `/${dailyMenuScreenPortraitPath}?${screenQuery}`
        : "";
  const dailyMenuDisabled = screenSettings?.enabled === false;
  const screenLinksDisabled = dailyMenuDisabled;

  /**
   * Odpoveď getDailyMenuList: { code, status, dailyMenu }.
   * dailyMenu = [{ _id, customerId, customerCode?, menus: [{ id, name, days, ... }], updatedAt }].
   * Vráti zploštený zoznam: jedna položka = jeden menu s idZaznamu, customerCode, customerId.
   * Odkaz na detail používa menu.id (konkrétne menu, napr. "dm-1769946408940-in9uiao").
   */
  const parseDailyMenuListResponse = (result) => {
    if (Array.isArray(result)) return result;
    const dailyMenuArray = result?.dailyMenu ?? result?.data?.dailyMenu ?? [];
    if (!Array.isArray(dailyMenuArray)) return [];
    return dailyMenuArray.flatMap((record) => {
      const menus = record?.menus ?? [];
      const customerCode = record?.customerCode ?? record?.customer?.code;
      const customerId = record?.customerId;
      return menus.map((menu) => ({
        ...menu,
        idZaznamu: record._id,
        customerCode,
        customerId,
      }));
    });
  };

  const loadMenus = async () => {
    setLoading(true);
    try {
      const result = await getDailyMenuList(customerCode);
      const list = parseDailyMenuListResponse(result);
      setMenus(list);
    } catch (e) {
      console.warn("getDailyMenuList failed", e);
      enqueueSnackbar(
        t("notifications.wasFailed", {
          defaultValue: "Nepodarilo sa načítať zoznam denných menu.",
        }),
        { variant: "error" },
      );
      setMenus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMenus();
  }, [customerCode]);

  useEffect(() => {
    let cancelled = false;
    const loadSettings = async () => {
      try {
        const fromBackend = await getDailyMenuSettings();
        if (cancelled) return;
        const merged =
          fromBackend?.settings ?? fromBackend ?? getDailyMenuScreenSettings();
        setScreenSettings(
          merged && typeof merged === "object"
            ? merged
            : getDailyMenuScreenSettings(),
        );
      } catch (_) {
        if (cancelled) return;
        setScreenSettings(getDailyMenuScreenSettings());
      }
    };
    loadSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  const inactiveTypeCodes = useMemo(() => {
    const mt = screenSettings?.menuTypes;
    const set = new Set();
    if (mt && typeof mt === "object") {
      for (const { code } of DAILY_MENU_TYPES) {
        if (mt[code] && mt[code].active === false) set.add(code);
      }
    }
    return set;
  }, [screenSettings]);

  /** Časy typu menu (od–do) z nastavení obrazovky, alebo "" ak nie sú zadané. */
  const getMenuTypeTimes = (code) => {
    const cfg = screenSettings?.menuTypes?.[code];
    const from = cfg?.from;
    const to = cfg?.to;
    if (from && to) return `${from} – ${to}`;
    if (from) return `od ${from}`;
    if (to) return `do ${to}`;
    return "";
  };

  const filteredMenus = useMemo(() => {
    const term = String(searchName || "").toLowerCase();
    let list = term
      ? menus.filter((m) => {
          const nameStr =
            (typeof m.name === "object" &&
              (m.name["sk-SK"] || m.name["en-US"])) ||
            "";
          const name = String(nameStr).toLowerCase();
          const rangeStr = getWeekRangeDisplay(m.weekStartDate).toLowerCase();
          const lastUpdated =
            m.days?.monday?.updatedAt || m.days?.tuesday?.updatedAt || "";
          return (
            name.includes(term) ||
            rangeStr.includes(term) ||
            (lastUpdated && lastUpdated.toLowerCase().includes(term))
          );
        })
      : [...menus];
    // Skry menu, ktorých typ je označený ako neaktívny v nastaveniach obrazovky.
    if (inactiveTypeCodes.size > 0) {
      list = list.filter((m) => !inactiveTypeCodes.has(m.menuType));
    }
    list.sort((a, b) => {
      const da = a.weekStartDate || "";
      const db = b.weekStartDate || "";
      return db.localeCompare(da);
    });
    return list;
  }, [menus, searchName, i18n.language, inactiveTypeCodes]);

  const thisWeekMondayIso = useMemo(() => getThisWeekMondayIso(), []);

  const handleAddMenu = () => {
    const newMenu = createNewMenu();
    enqueueSnackbar(
      t("notifications.wasSuccessful", { defaultValue: "Saved" }),
      {
        variant: "success",
      },
    );
    navigate(`/customer/dsok/daily-menu/${newMenu.id}`);
  };

  const handleEdit = (menuId) => {
    navigate(`/customer/dsok/daily-menu/${menuId}`);
  };

  /** Načíta menu (z backendu alebo storage), naklonuje, vymaže weekStartDate, uloží do storage a prejde na detail. */
  const handleCopyMenu = async (menu) => {
    const idZaznamu = menu.idZaznamu ?? menu._id;
    const menuId = menu.id ?? idZaznamu;
    if (!menuId) return;
    setCopyingMenuId(menuId);
    try {
      let sourceMenu = null;
      if (idZaznamu) {
        try {
          const raw = await getDailyMenuById(idZaznamu);
          const record = raw?.dailyMenu ?? raw?.data ?? raw;
          const menusList = record?.menus ?? [];
          sourceMenu =
            menusList.find((m) => (m.id ?? m._id) === menuId) ?? menusList[0];
        } catch (_) {
          // fallback na storage
        }
      }
      if (!sourceMenu) {
        sourceMenu = getStoredMenu(menuId);
      }
      if (!sourceMenu) {
        enqueueSnackbar(
          t("notifications.wasFailed", {
            defaultValue: "Nepodarilo sa načítať menu.",
          }),
          { variant: "error" },
        );
        return;
      }
      const cloned = JSON.parse(JSON.stringify(sourceMenu));
      cloned.weekStartDate = undefined;
      cloned.id = `dm-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      delete cloned._id;
      saveMenu(cloned);
      enqueueSnackbar(
        t("notifications.wasSuccessful", { defaultValue: "Menu skopírované." }),
        { variant: "success" },
      );
      navigate(`/customer/dsok/daily-menu/${cloned.id}`);
    } catch (e) {
      console.warn("copy menu failed", e);
      enqueueSnackbar(
        t("notifications.wasFailed", {
          defaultValue: "Nepodarilo sa skopírovať menu.",
        }),
        { variant: "error" },
      );
    } finally {
      setCopyingMenuId(null);
    }
  };

  /**
   * Vygeneruje a zverejní WEB export pre celého zákazníka naraz – 6 súborov
   * (breakfast/lunch/dinner × aktuálny týždeň "_2" / nasledujúci týždeň "_1").
   * Kombinácie bez existujúceho menu sa preskočia.
   */
  const handlePublishWebExport = async () => {
    setPublishingWebExport(true);
    try {
      const result = await publishDailyMenuWebExport();
      setWebExportResult(result);
    } catch (e) {
      enqueueSnackbar(
        t("notifications.wasFailed", {
          defaultValue: "Nepodarilo sa zverejniť WEB export.",
        }),
        { variant: "error" },
      );
    } finally {
      setPublishingWebExport(false);
    }
  };

  const webExportRows = useMemo(() => {
    if (!webExportResult) return [];
    const all = [
      ...(webExportResult.published || []).map((item) => ({
        ...item,
        publishedOk: true,
      })),
      ...(webExportResult.skipped || []).map((item) => ({
        ...item,
        publishedOk: false,
      })),
    ];
    return all.sort((a, b) => {
      const keyA = (a.fileName || "").replace(/\.json$/, "");
      const keyB = (b.fileName || "").replace(/\.json$/, "");
      return WEB_EXPORT_ROW_ORDER.indexOf(keyA) - WEB_EXPORT_ROW_ORDER.indexOf(keyB);
    });
  }, [webExportResult]);

  const handleScreenSettings = () => {
    navigate("/customer/dsok/daily-menu/screen-settings");
  };

  const handleRefreshScreen = async () => {
    setRefreshingScreen(true);
    try {
      await refreshDailyMenuScreen();
      enqueueSnackbar(
        t("pages.dsokDailyMenu.refreshScreenSent", {
          defaultValue: "Obrazovky boli obnovené.",
        }),
        { variant: "success" },
      );
    } catch (e) {
      enqueueSnackbar(
        t("notifications.wasFailed", {
          defaultValue: "Nepodarilo sa obnoviť obrazovky.",
        }),
        { variant: "error" },
      );
    } finally {
      setRefreshingScreen(false);
    }
  };

  const handleDeleteConfirm = async (menu) => {
    hideDialog();
    const backendId = menu?.id;
    if (!backendId) {
      enqueueSnackbar(
        t("notifications.wasFailed", { defaultValue: "Chýba ID menu." }),
        { variant: "error" },
      );
      return;
    }
    setDeletingMenuId(backendId);
    try {
      await deleteDailyMenuById(menu.id);
      deleteMenuFromStorage(menu.id);
      if (menu.idZaznamu && menu.idZaznamu !== menu.id) {
        deleteMenuFromStorage(menu.idZaznamu);
      }
      loadMenus();
      enqueueSnackbar(
        t("notifications.wasSuccessful", {
          defaultValue: "Denné menu bolo zmazané.",
        }),
        { variant: "success" },
      );
    } catch (e) {
      const body = e?.response?.data;
      const message =
        (body && typeof body === "object" && body.message) ||
        (typeof body === "string" ? body : null) ||
        e?.message ||
        t("notifications.wasFailed", { defaultValue: "Zmazanie zlyhalo." });
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setDeletingMenuId(null);
    }
  };

  const deleteConfirmation = (menu) => {
    showDialog({
      variant: "confirm",
      title: t("pages.dsokDailyMenu.confirmDelete", {
        defaultValue: "Naozaj chcete zmazať toto denné menu?",
      }),
      onYes: () => handleDeleteConfirm(menu),
      onNo: hideDialog,
    });
  };

  const publishWebExportConfirmation = () => {
    showDialog({
      variant: "confirm",
      title: t("pages.dsokDailyMenu.confirmPublishWebExport", {
        defaultValue:
          "Naozaj chcete publikovať WEB export? Prepíše existujúce verejné súbory pre aktuálny a nasledujúci týždeň.",
      }),
      onYes: () => {
        hideDialog();
        handlePublishWebExport();
      },
      onNo: hideDialog,
    });
  };

  const getMenuDisplayName = (menu) => {
    if (!menu?.name) return menu?.id || "";
    if (typeof menu.name === "string") return menu.name;
    return menu.name["sk-SK"] || menu.name["en-US"] || menu.id;
  };

  return (
    <React.Fragment>
      <Typography variant="h2" mb={3}>
        {t("sidebar.menuItem.dsokDailyMenu", { defaultValue: "Daily Menu" })}
      </Typography>
      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        mb={2}
        spacing={1}
      >
        <Card sx={{ mb: 0, width: "50%" }}>
          <JumboSearch
            page="dsokDailyMenus"
            onChange={setSearchName}
            sx={{ width: "100%" }}
            placeholder={t("pages.presentation.searchPresentation")}
          />
        </Card>
        <Tooltip title={t("pages.menus.addMenu", { defaultValue: "Add Menu" })}>
          <IconButton onClick={handleAddMenu}>
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={t("pages.dsokDailyMenu.screenSettings", {
            defaultValue: "Nastavenie denného menu",
          })}
        >
          <IconButton onClick={handleScreenSettings}>
            <SettingsIcon />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={
            dailyMenuDisabled
              ? t("pages.dsokDailyMenu.dailyMenuDisabled", {
                  defaultValue: "Daily menu disabled",
                })
              : t("pages.dsokDailyMenu.openScreenLandscape", {
                  defaultValue: "Denné menu – Landscape",
                })
          }
        >
          <span>
            <IconButton
              component={dailyMenuScreenLandscapeUrl ? "a" : "button"}
              href={dailyMenuScreenLandscapeUrl || undefined}
              target={dailyMenuScreenLandscapeUrl ? "_blank" : undefined}
              rel={
                dailyMenuScreenLandscapeUrl ? "noopener noreferrer" : undefined
              }
              type={dailyMenuScreenLandscapeUrl ? undefined : "button"}
              size="small"
              disableRipple
              disabled={screenLinksDisabled}
              aria-label={t("pages.dsokDailyMenu.openScreenLandscape", {
                defaultValue: "Denné menu – Landscape",
              })}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (screenLinksDisabled || !dailyMenuScreenLandscapeUrl) return;
                window.open(
                  dailyMenuScreenLandscapeUrl,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            >
              <StayCurrentLandscapeIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={
            dailyMenuDisabled
              ? t("pages.dsokDailyMenu.dailyMenuDisabled", {
                  defaultValue: "Daily menu disabled",
                })
              : t("pages.dsokDailyMenu.openScreenPortrait", {
                  defaultValue: "Daily menu – Portrait",
                })
          }
        >
          <span>
            <IconButton
              component={dailyMenuScreenPortraitUrl ? "a" : "button"}
              href={dailyMenuScreenPortraitUrl || undefined}
              target={dailyMenuScreenPortraitUrl ? "_blank" : undefined}
              rel={
                dailyMenuScreenPortraitUrl ? "noopener noreferrer" : undefined
              }
              type={dailyMenuScreenPortraitUrl ? undefined : "button"}
              size="small"
              disableRipple
              disabled={screenLinksDisabled}
              aria-label={t("pages.dsokDailyMenu.openScreenPortrait", {
                defaultValue: "Daily menu – Portrait",
              })}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (screenLinksDisabled || !dailyMenuScreenPortraitUrl) return;
                window.open(
                  dailyMenuScreenPortraitUrl,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
            >
              <StayCurrentPortraitIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={t("pages.dsokDailyMenu.refreshScreen", {
            defaultValue: "Obnoviť obrazovky",
          })}
        >
          <span>
            <IconButton
              size="small"
              disabled={refreshingScreen}
              aria-label={t("pages.dsokDailyMenu.refreshScreen", {
                defaultValue: "Obnoviť obrazovky",
              })}
              onClick={handleRefreshScreen}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        {enableWebExport && (
          <Tooltip
            title={t("pages.dsokDailyMenu.publishWebExport", {
              defaultValue: "Publikovať WEB export",
            })}
          >
            <span>
              <IconButton
                size="small"
                disabled={publishingWebExport}
                aria-label={t("pages.dsokDailyMenu.publishWebExport", {
                  defaultValue: "Publikovať WEB export",
                })}
                onClick={publishWebExportConfirmation}
              >
                <CloudUploadIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Dialog
        open={!!webExportResult}
        onClose={() => setWebExportResult(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("pages.dsokDailyMenu.webExportResultTitle", {
            defaultValue: "WEB Export – vygenerované súbory",
          })}
        </DialogTitle>
        <DialogContent dividers>
          <List dense disablePadding>
            {webExportRows.map((row) => (
              <ListItem
                key={row.fileName}
                disableGutters
                secondaryAction={
                  row.publishedOk ? (
                    <IconButton
                      component="a"
                      href={row.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      aria-label={t("buttons.open", { defaultValue: "Otvoriť" })}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {row.publishedOk ? (
                    <CheckCircleIcon color="success" fontSize="small" />
                  ) : (
                    <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={row.fileName}
                  secondary={
                    row.publishedOk
                      ? row.url
                      : t("pages.dsokDailyMenu.webExportSkippedReason", {
                          defaultValue: "Preskočené – pre tento týždeň a typ neexistuje menu.",
                        })
                  }
                  secondaryTypographyProps={{
                    sx: { wordBreak: "break-all" },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWebExportResult(null)}>
            {t("buttons.close", { defaultValue: "Zavrieť" })}
          </Button>
        </DialogActions>
      </Dialog>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredMenus?.length ? (
        filteredMenus.map((menu) => {
          const idZaznamu = menu.idZaznamu ?? menu._id;
          const menuId = menu.id ?? idZaznamu;
          const isActiveWeek = menu.weekStartDate === thisWeekMondayIso;
          return (
            <Card
              key={menuId + (menu.weekStartDate || "")}
              sx={{
                p: 2,
                mb: 1,
                ...(isActiveWeek && {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "& .MuiTypography-root": { color: "inherit" },
                  "& .MuiIconButton-root": { color: "inherit" },
                }),
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
              >
                <Stack
                  direction="row"
                  spacing={4}
                  alignItems="center"
                  sx={{
                    flex: 1,
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "inherit",
                  }}
                  component={Link}
                  to={`/customer/dsok/daily-menu/${menuId}`}
                >
                  <Stack>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t("pages.dsokDailyMenu.odDo", {
                        defaultValue: "Od – Do",
                      })}
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {getWeekRangeDisplay(menu.weekStartDate)}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t("pages.presentation.label", { defaultValue: "Name" })}
                    </Typography>
                    <Typography variant="body1">
                      {getMenuDisplayName(menu)}
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {t("pages.dsokDailyMenu.menuType", {
                        defaultValue: "Typ menu",
                      })}
                    </Typography>
                    <Typography variant="body1">
                      {menu.menuType
                        ? `${getMenuTypeLabel(menu.menuType) || "—"}${
                            getMenuTypeTimes(menu.menuType)
                              ? ` (${getMenuTypeTimes(menu.menuType)})`
                              : ""
                          }`
                        : "—"}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Tooltip title={t("buttons.edit")}>
                    <IconButton
                      component={Link}
                      to={`/customer/dsok/daily-menu/${menuId}`}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={t("pages.dsokDailyMenu.copyMenu", {
                      defaultValue: "Kopírovať menu",
                    })}
                  >
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleCopyMenu(menu);
                      }}
                      disabled={copyingMenuId === menuId}
                      size="small"
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("buttons.delete")}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        deleteConfirmation(menu);
                      }}
                      disabled={deletingMenuId === (idZaznamu ?? menuId)}
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </Card>
          );
        })
      ) : (
        <JumboListNoDataPlaceHolder />
      )}
    </React.Fragment>
  );
};

export default DsokDailyMenuList;
